import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, HomeIcon, PresentationIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// --- Animation Variants ---
const typewriterContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.3
    }
  }
};
const typewriterCharacter = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 12,
      stiffness: 200
    }
  }
};
const listContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.8
    }
  }
};
const listItem = {
  hidden: {
    opacity: 0,
    x: -20,
    filter: 'blur(5px)'
  },
  show: {
    opacity: 1,
    x: 0,
    filter: 'blur(0px)',
    transition: {
      type: 'spring',
      damping: 20
    }
  }
};
const iconVariant = {
  hidden: {
    scale: 0,
    rotate: -45,
    opacity: 0
  },
  show: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 15,
      stiffness: 100,
      delay: 0.5
    }
  }
};
// --- Sub-Components ---
const TypewriterText = ({
  text,
  className
}: {
  text: string;
  className?: string;
}) => <motion.h2 variants={typewriterContainer} initial="hidden" animate="show" className={className} data-id="element-666">
    {text.split('').map((char, i) => <motion.span key={i} variants={typewriterCharacter} data-id="element-667">
        {char}
      </motion.span>)}
  </motion.h2>;
const WhiteSplat = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 200 200" fill="white" xmlns="http://www.w3.org/2000/svg" className={className} style={{
  filter: 'blur(1px)'
}} data-id="element-668">
    <path d="M94.6,12.8c-7.3,1.6-13.9,6.5-17.8,13.2c-2.3,3.9-3.2,8.5-2.6,13c0.4,3.1,1.5,6,3.2,8.6c-4.4-1.8-9.3-2.1-13.9-0.8 c-7.6,2.1-13.6,8.2-16.1,15.7c-1.3,3.9-1.5,8.1-0.6,12.1c-5.1-2.9-11.2-3.4-16.7-1.3c-8.6,3.3-14.7,11.2-15.9,20.3 c-0.6,4.6,0.3,9.3,2.5,13.4c-4.8-0.9-9.8,0.2-13.9,3.1c-6.8,4.8-10.1,13.2-8.5,21.3c0.9,4.4,3.6,8.3,7.3,10.9 c-3.6,2.6-6.1,6.6-6.8,11.1c-1.1,7.6,2.2,15.2,8.6,19.6c4.1,2.8,9,3.9,13.9,3.1c1.3,4.4,4.2,8.2,8.2,10.6 c6.8,4.1,15.3,3.7,21.8-1.1c1.8,4.1,5.2,7.4,9.5,9.2c7.3,3.1,15.7,1.8,21.8-3.4c2.8,3.6,6.9,6,11.4,6.6c7.8,1.1,15.6-2.6,19.6-9.2 c3.4,2.9,7.9,4.2,12.4,3.6c7.6-1,13.9-6.4,16.3-13.7c1.3-3.9,1.1-8.2-0.5-12c4.6,1.6,9.7,1.1,13.9-1.5c6.6-4,10.1-11.7,8.8-19.3 c-0.6-3.9-2.9-7.3-6.1-9.6c4.4-1.1,8.3-4.1,10.4-8.2c3.6-6.9,2.8-15.3-2.1-21.5c-2.6-3.3-6.3-5.6-10.4-6.5 c3.3-3.1,5.1-7.6,4.8-12.1c-0.4-7.4-5.6-13.7-12.6-15.8c-3.6-1.1-7.4-1-10.9,0.3c1.6-4.1,1.3-8.8-0.9-12.6 c-3.7-6.6-11.4-10.1-18.8-8.8c-3.9,0.7-7.4,2.9-9.9,6.1c-0.4-4.6-2.6-8.8-6.1-11.7C114.7,13.3,104.6,9.8,94.6,12.8z" data-id="element-669" />
  </svg>;
// Filmmaker SVG Illustration
const FilmmakerIllustration = () => <motion.svg initial={{
  opacity: 0,
  scale: 0.8
}} animate={{
  opacity: 1,
  scale: 1
}} transition={{
  delay: 0.5,
  duration: 0.8,
  type: 'spring'
}} viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full max-w-[400px] max-h-[400px]" data-id="element-670">
    {/* Tripod */}
    <motion.g initial={{
    y: 20,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    delay: 0.6
  }} data-id="element-671">
      <line x1="200" y1="220" x2="140" y2="370" stroke="#60a5fa" strokeWidth="3" strokeLinecap="round" data-id="element-672" />
      <line x1="200" y1="220" x2="260" y2="370" stroke="#60a5fa" strokeWidth="3" strokeLinecap="round" data-id="element-673" />
      <line x1="200" y1="220" x2="200" y2="375" stroke="#60a5fa" strokeWidth="3" strokeLinecap="round" data-id="element-674" />
      <line x1="160" y1="310" x2="240" y2="310" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.6" data-id="element-675" />
    </motion.g>
    {/* Camera Body */}
    <motion.g initial={{
    scale: 0,
    opacity: 0
  }} animate={{
    scale: 1,
    opacity: 1
  }} transition={{
    delay: 0.8,
    type: 'spring'
  }} data-id="element-676">
      <rect x="155" y="150" width="90" height="70" rx="8" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="2" data-id="element-677" />
      <rect x="160" y="155" width="80" height="60" rx="5" fill="#0f2440" data-id="element-678" />
      {/* Lens */}
      <circle cx="200" cy="185" r="22" fill="#0a1628" stroke="#60a5fa" strokeWidth="2" data-id="element-679" />
      <circle cx="200" cy="185" r="15" fill="#0f2440" stroke="#3b82f6" strokeWidth="1.5" data-id="element-680" />
      <circle cx="200" cy="185" r="8" fill="#1e40af" opacity="0.6" data-id="element-681" />
      <motion.circle cx="200" cy="185" r="4" fill="#60a5fa" animate={{
      opacity: [0.4, 1, 0.4]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-682" />
      {/* Viewfinder */}
      <rect x="248" y="158" width="25" height="18" rx="3" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="1.5" data-id="element-683" />
      {/* Record light */}
      <motion.circle cx="170" cy="162" r="4" fill="#ef4444" animate={{
      opacity: [1, 0.3, 1]
    }} transition={{
      duration: 1.5,
      repeat: Infinity
    }} data-id="element-684" />
      {/* Top handle */}
      <rect x="180" y="140" width="40" height="12" rx="4" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="1.5" data-id="element-685" />
    </motion.g>
    {/* Person silhouette */}
    <motion.g initial={{
    x: -30,
    opacity: 0
  }} animate={{
    x: 0,
    opacity: 1
  }} transition={{
    delay: 1,
    duration: 0.6
  }} data-id="element-686">
      {/* Head */}
      <circle cx="120" cy="160" r="18" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="1.5" data-id="element-687" />
      {/* Body */}
      <path d="M120 178 Q120 200 110 240 Q108 250 115 250 L125 250 Q132 250 130 240 Q120 200 120 178Z" fill="#1e3a5f" stroke="#60a5fa" strokeWidth="1" data-id="element-688" />
      {/* Arm reaching to camera */}
      <path d="M130 195 Q140 190 155 185" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-689" />
      {/* Other arm */}
      <path d="M110 195 Q100 210 95 225" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-690" />
      {/* Cap */}
      <path d="M104 155 Q120 145 136 155" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" fill="none" data-id="element-691" />
    </motion.g>
    {/* Film reel decorations */}
    <motion.g animate={{
    rotate: 360
  }} transition={{
    duration: 8,
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-692">
      <circle cx="320" cy="100" r="30" fill="none" stroke="#60a5fa" strokeWidth="1.5" opacity="0.3" data-id="element-693" />
      <circle cx="320" cy="100" r="10" fill="none" stroke="#60a5fa" strokeWidth="1" opacity="0.3" data-id="element-694" />
      <line x1="320" y1="70" x2="320" y2="80" stroke="#60a5fa" strokeWidth="1" opacity="0.3" data-id="element-695" />
      <line x1="320" y1="120" x2="320" y2="130" stroke="#60a5fa" strokeWidth="1" opacity="0.3" data-id="element-696" />
      <line x1="290" y1="100" x2="300" y2="100" stroke="#60a5fa" strokeWidth="1" opacity="0.3" data-id="element-697" />
      <line x1="340" y1="100" x2="350" y2="100" stroke="#60a5fa" strokeWidth="1" opacity="0.3" data-id="element-698" />
    </motion.g>
    {/* Clapperboard */}
    <motion.g initial={{
    rotate: -20,
    opacity: 0
  }} animate={{
    rotate: 0,
    opacity: 0.4
  }} transition={{
    delay: 1.2
  }} data-id="element-699">
      <rect x="290" y="260" width="60" height="45" rx="4" fill="none" stroke="#60a5fa" strokeWidth="1.5" data-id="element-700" />
      <rect x="290" y="250" width="60" height="15" rx="3" fill="none" stroke="#60a5fa" strokeWidth="1.5" data-id="element-701" />
      <line x1="300" y1="250" x2="305" y2="265" stroke="#60a5fa" strokeWidth="1" data-id="element-702" />
      <line x1="315" y1="250" x2="320" y2="265" stroke="#60a5fa" strokeWidth="1" data-id="element-703" />
      <line x1="330" y1="250" x2="335" y2="265" stroke="#60a5fa" strokeWidth="1" data-id="element-704" />
    </motion.g>
  </motion.svg>;
// Bubbles + Foam for Red Ocean
const RedOceanEffects = () => <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-705">
    {/* White Foam at Top */}
    <div className="absolute top-0 left-0 w-full h-32 z-10" data-id="element-706">
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-full" data-id="element-707">
        <path d="M0,0 L1440,0 L1440,40 Q1380,80 1300,50 Q1200,20 1100,60 Q1000,90 900,55 Q800,20 700,65 Q600,100 500,50 Q400,10 300,55 Q200,90 100,45 Q50,20 0,50 Z" fill="white" fillOpacity="0.08" data-id="element-708" />
        <path d="M0,0 L1440,0 L1440,25 Q1350,60 1250,35 Q1150,10 1050,45 Q950,75 850,40 Q750,5 650,50 Q550,85 450,35 Q350,0 250,40 Q150,70 50,30 Q20,15 0,35 Z" fill="white" fillOpacity="0.12" data-id="element-709" />
        <path d="M0,0 L1440,0 L1440,15 Q1400,30 1320,18 Q1200,5 1100,22 Q1000,38 900,15 Q800,0 700,20 Q600,35 500,12 Q400,0 300,18 Q200,32 100,10 Q50,2 0,15 Z" fill="white" fillOpacity="0.18" data-id="element-710" />
      </svg>
    </div>
    {/* Animated Bubbles */}
    {[...Array(18)].map((_, i) => {
    const size = Math.random() * 14 + 4;
    const left = Math.random() * 100;
    const delay = Math.random() * 6;
    const duration = Math.random() * 6 + 6;
    return <motion.div key={i} className="absolute rounded-full border border-red-300/30 bg-red-200/10" style={{
      width: size,
      height: size,
      left: `${left}%`,
      bottom: -20
    }} animate={{
      y: [0, -(window.innerHeight + 40)],
      x: [0, Math.sin(i) * 30, 0],
      opacity: [0, 0.6, 0.4, 0]
    }} transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeOut'
    }} data-id="element-711" />;
  })}
  </div>;
// Profit Charts for Blue Ocean
const BlueOceanCharts = () => <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-712">
    {/* Rising chart lines */}
    {[...Array(5)].map((_, i) => {
    const startX = 10 + i * 20;
    return <motion.svg key={i} className="absolute bottom-0 opacity-10" style={{
      left: `${startX}%`,
      width: '20%',
      height: '60%'
    }} viewBox="0 0 100 200" preserveAspectRatio="none" initial={{
      opacity: 0
    }} animate={{
      opacity: 0.1
    }} transition={{
      delay: i * 0.3
    }} data-id="element-713">
          <motion.path d={`M10,200 Q25,${180 - i * 20} 40,${150 - i * 15} Q55,${120 - i * 20} 70,${80 - i * 10} Q85,${50 - i * 5} 95,${20 - i * 3}`} stroke="#38bdf8" strokeWidth="2" fill="none" strokeLinecap="round" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 2,
        delay: 0.5 + i * 0.4,
        ease: 'easeOut'
      }} data-id="element-714" />
          {/* Arrow tip */}
          <motion.polygon points={`${90 - i * 2},${25 - i * 3} ${95},${15 - i * 3} ${98},${28 - i * 3}`} fill="#38bdf8" initial={{
        opacity: 0
      }} animate={{
        opacity: 0.8
      }} transition={{
        delay: 2.5 + i * 0.4
      }} data-id="element-715" />
        </motion.svg>;
  })}
    {/* Subtle bar chart in background */}
    <div className="absolute bottom-10 right-10 flex items-end gap-3 opacity-[0.07]" data-id="element-716">
      {[40, 55, 45, 70, 60, 85, 75, 95].map((h, i) => <motion.div key={i} className="w-6 bg-cyan-400 rounded-t" initial={{
      height: 0
    }} animate={{
      height: h * 2
    }} transition={{
      delay: 1 + i * 0.15,
      duration: 0.6,
      ease: 'easeOut'
    }} data-id="element-717" />)}
    </div>
  </div>;
const WaveBackground = ({
  color = 'blue'
}: {
  color?: 'blue' | 'red' | 'dark';
}) => {
  const isRed = color === 'red';
  const isDark = color === 'dark';
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-718">
      <div className={`absolute inset-0 opacity-30 mix-blend-overlay ${isRed ? 'bg-red-500' : isDark ? 'bg-blue-900' : 'bg-cyan-400'}`} style={{
      filter: 'url(#turbulence)'
    }} data-id="element-719" />
      {[...Array(5)].map((_, i) => <motion.div key={i} className={`absolute rounded-full blur-[60px] opacity-20 ${isRed ? 'bg-red-500' : isDark ? 'bg-indigo-900' : 'bg-blue-400'}`} style={{
      width: Math.random() * 300 + 100,
      height: Math.random() * 300 + 100,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      y: [0, -50, 0],
      x: [0, 30, 0],
      scale: [1, 1.2, 1]
    }} transition={{
      duration: Math.random() * 10 + 10,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-720" />)}
      <div className="absolute bottom-0 left-0 w-full h-48 opacity-40" data-id="element-721">
        <motion.svg viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-full" preserveAspectRatio="none" animate={{
        x: [-50, 0, -50]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-722">
          <path fill={isRed ? '#7f1d1d' : isDark ? '#1e1b4b' : '#0c4a6e'} fillOpacity="0.5" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-723" />
        </motion.svg>
        <motion.svg viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-full" preserveAspectRatio="none" animate={{
        x: [0, -50, 0]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-724">
          <path fill={isRed ? '#991b1b' : isDark ? '#312e81' : '#075985'} fillOpacity="0.3" d="M0,256L48,245.3C96,235,192,213,288,192C384,171,480,149,576,160C672,171,768,213,864,224C960,235,1056,213,1152,192C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-725" />
        </motion.svg>
      </div>
    </div>;
};
// --- Main Component ---
const StrategySlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [{
    id: 'cover',
    title: 'GRUPO REFLEX',
    subtitle: 'Océanos Rojos, Océanos Azules y Cisnes Negros',
    type: 'cover'
  }, {
    id: 'intro',
    title: 'Grupo Reflex hoy',
    bullets: ['Agencia de generación de contenido para marketing digital', 'Fábrica creativa: videos, reels, fotografía, diseño y copys', 'Contenido con consistencia visual y narrativa orientado a resultados'],
    type: 'content'
  }, {
    id: 'red-ocean',
    title: 'Océano Rojo',
    bullets: ['Mercado saturado de producción audiovisual con baja barrera de entrada', 'Eventos sociales y sesiones personales: competencia por precio (comoditizado)', 'Creadores e influencers desplazan parte del contenido tradicional en el interior'],
    icon: 'https://cdn.magicpatterns.com/uploads/jfvyBSYSvWtctQaW1mpJYp/ROJO.png',
    type: 'icon-content',
    theme: 'red'
  }, {
    id: 'blue-ocean',
    title: 'Océano Azul',
    bullets: ['Migración de "marca personal" a agencia estructurada (procesos y consistencia)', 'Especialización en industrias que demandan calidad y continuidad', 'Diferenciación por narrativa, ejecución profesional y estándar de marca'],
    icon: 'https://cdn.magicpatterns.com/uploads/oFWaSmYQZ1W2M9h6yZzvBj/AZUL.png',
    type: 'icon-content',
    theme: 'blue'
  }, {
    id: 'bridge',
    title: 'Transformación Digital',
    diagram: {
      left: 'Servicios comoditizados',
      center: 'Procesos + Calidad + Narrativa + Consistencia',
      right: 'Mercados de alto valor'
    },
    quote: 'Pasamos de competir por precio a competir por valor y resultados.',
    type: 'diagram'
  }, {
    id: 'black-swan',
    title: 'Cisne Negro',
    bullets: ['Evolucionar de proveedor de contenido a socio estratégico de crecimiento', 'Convertir experiencia creativa en metodologías repetibles y medibles', 'Integrar tecnología e inteligencia artificial para eficiencia y escalabilidad', 'Ampliar hacia consultoría y acompañamiento de proyectos'],
    icon: 'https://cdn.magicpatterns.com/uploads/4MZsW1f4XUdhfherhKSZhr/BLACK_SWAN.png',
    type: 'icon-content',
    theme: 'black'
  }, {
    id: 'meta-swan',
    title: 'Cisne Negro 2.0',
    bullets: ['Crear un modelo replicable que no dependa del fundador para operar', 'Construir activos digitales que se puedan reutilizar y escalar', 'Elevar el estándar del mercado mediante innovación y excelencia'],
    icon: 'https://cdn.magicpatterns.com/uploads/4MZsW1f4XUdhfherhKSZhr/BLACK_SWAN.png',
    type: 'icon-content',
    theme: 'black'
  }, {
    id: 'closing',
    title: 'El Futuro Comienza Ahora',
    lines: ['Estrategia: salir de la saturación, construir diferenciación y escalar con propósito.', 'Grupo Reflex: creatividad, estructura e innovación para generar resultados.'],
    type: 'closing'
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const currentData = slides[currentSlide];
  const getBackgroundClass = () => {
    if (currentData.id === 'red-ocean') return 'bg-gradient-to-br from-[#1a0505] via-[#2d0a0a] to-[#0a0000]';
    if (currentData.id === 'blue-ocean') return 'bg-gradient-to-br from-[#051a2d] via-[#0a1e3d] to-[#000a1a]';
    if (currentData.id === 'bridge') return 'bg-gradient-to-r from-[#2d0a0a] via-[#150815] to-[#0a1e3d]';
    if (currentData.theme === 'black') return 'bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#111]';
    return 'bg-gradient-to-br from-[#0a0f1e] via-[#0d1526] to-[#000000]';
  };
  return <div className={`fixed inset-0 w-full h-screen text-white overflow-hidden font-sans transition-colors duration-1000 ${getBackgroundClass()}`} data-id="element-726">
      <svg className="hidden" data-id="element-727">
        <filter id="turbulence" data-id="element-728">
          <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="3" result="noise" data-id="element-729" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="10" data-id="element-730" />
        </filter>
      </svg>

      {/* Navigation Bar */}
      <div className="absolute top-6 left-6 z-50 flex gap-4" data-id="element-731">
        <Link to="/" className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full transition-all text-sm font-medium border border-white/10" data-id="element-732">
          <HomeIcon className="w-4 h-4" data-id="element-733" />
          <span data-id="element-734">Home</span>
        </Link>
        <Link to="/slideshow" className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full transition-all text-sm font-medium border border-white/10" data-id="element-735">
          <PresentationIcon className="w-4 h-4" data-id="element-736" />
          <span data-id="element-737">Legado Creativo</span>
        </Link>
      </div>

      {/* Dynamic Background Elements */}
      <AnimatePresence mode="wait" data-id="element-738">
        <motion.div key={currentData.id + '-bg'} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1
      }} className="absolute inset-0 z-0" data-id="element-739">
          {currentData.id === 'red-ocean' && <>
              <WaveBackground color="red" data-id="element-740" />
              <RedOceanEffects data-id="element-741" />
            </>}
          {currentData.id === 'blue-ocean' && <>
              <WaveBackground color="blue" data-id="element-742" />
              <BlueOceanCharts data-id="element-743" />
            </>}
          {currentData.id === 'bridge' && <WaveBackground color="dark" data-id="element-744" />}
          {(currentData.theme === 'black' || currentData.id === 'intro' || currentData.id === 'cover') && <WaveBackground color="dark" data-id="element-745" />}
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.04]" data-id="element-746" />
        </motion.div>
      </AnimatePresence>

      {/* Main Content Area */}
      <AnimatePresence mode="wait" data-id="element-747">
        <motion.div key={currentSlide} initial={{
        opacity: 0,
        y: 30,
        filter: 'blur(8px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} exit={{
        opacity: 0,
        y: -30,
        filter: 'blur(8px)'
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative z-10 w-full h-full flex flex-col items-center justify-center p-8 md:p-16" data-id="element-748">
          <div className="max-w-7xl w-full mx-auto" data-id="element-749">
            {/* COVER SLIDE */}
            {currentData.type === 'cover' && <div className="text-center space-y-8" data-id="element-750">
                <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 1,
              type: 'spring'
            }} data-id="element-751">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/weJPa2B6KxMjVxBcMFKGGT/LOGO_GRUPO_REFLEX.png" alt="Grupo Reflex" className="h-32 mx-auto mb-8 brightness-0 invert opacity-90" data-id="element-752" />
                </motion.div>
                <div className="overflow-hidden" data-id="element-753">
                  <TypewriterText text={currentData.title} className="text-6xl md:text-8xl font-bold tracking-tight bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent" data-id="element-754" />
                </div>
                <motion.div initial={{
              width: 0
            }} animate={{
              width: 128
            }} transition={{
              delay: 1,
              duration: 0.8
            }} className="h-1 bg-blue-500 mx-auto rounded-full" data-id="element-755" />
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.5,
              duration: 0.8
            }} className="text-2xl md:text-3xl text-gray-400 font-light tracking-wide" data-id="element-756">
                  {currentData.subtitle}
                </motion.p>
              </div>}

            {/* CONTENT / ICON-CONTENT SLIDES */}
            {(currentData.type === 'content' || currentData.type === 'icon-content') && <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-757">
                <div className="space-y-8" data-id="element-758">
                  <TypewriterText text={currentData.title} className="text-5xl md:text-6xl font-bold text-white leading-tight" data-id="element-759" />
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: 96
              }} transition={{
                delay: 0.8,
                duration: 0.6
              }} className={`h-1 rounded-full ${currentData.theme === 'red' ? 'bg-red-500' : currentData.theme === 'black' ? 'bg-gray-400' : 'bg-blue-500'}`} data-id="element-760" />
                  <motion.ul variants={listContainer} initial="hidden" animate="show" className="space-y-6" data-id="element-761">
                    {currentData.bullets?.map((bullet, idx) => <motion.li key={idx} variants={listItem} className="flex items-start gap-4 text-xl md:text-2xl text-gray-300 font-light leading-relaxed" data-id="element-762">
                        <span className={`mt-2 w-2 h-2 rounded-full shrink-0 ${currentData.theme === 'red' ? 'bg-red-400' : currentData.theme === 'black' ? 'bg-gray-400' : 'bg-blue-400'}`} data-id="element-763" />
                        {bullet}
                      </motion.li>)}
                  </motion.ul>
                </div>

                {/* Right side: Icon or Illustration */}
                {currentData.type === 'icon-content' && currentData.icon && <div className={`flex justify-center items-center relative ${currentData.theme === 'black' ? 'h-[500px]' : 'h-[400px]'}`} data-id="element-764">
                    {currentData.theme === 'black' ? <motion.div initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.8,
                delay: 0.3,
                type: 'spring',
                damping: 15
              }} className="relative w-full h-full flex items-center justify-center" data-id="element-765">
                        <motion.img src={currentData.icon} alt="Cisne Negro" className="w-[20rem] h-[20rem] md:w-[28rem] md:h-[28rem] object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]" data-id="element-766" />
                      </motion.div> : <>
                        <div className={`absolute inset-0 bg-gradient-to-br ${currentData.theme === 'red' ? 'from-red-500/30 to-orange-500/30' : 'from-blue-500/30 to-cyan-500/30'} blur-[80px] rounded-full opacity-50`} data-id="element-767" />
                        <motion.img variants={iconVariant} initial="hidden" animate="show" src={currentData.icon} alt="Icon" className="w-64 h-64 md:w-96 md:h-96 object-contain relative z-10 drop-shadow-2xl" data-id="element-768" />
                      </>}
                  </div>}

                {/* Intro slide: Filmmaker illustration on the right */}
                {currentData.type === 'content' && currentData.id === 'intro' && <div className="flex justify-center items-center h-[400px]" data-id="element-769">
                      <FilmmakerIllustration data-id="element-770" />
                    </div>}

                {/* Generic content placeholder (for any other content slides without special handling) */}
                {currentData.type === 'content' && currentData.id !== 'intro' && <div className="flex justify-center items-center" data-id="element-771">
                      <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.5
              }} className="w-full h-64 md:h-96 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm flex items-center justify-center p-8 relative overflow-hidden" data-id="element-772">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/weJPa2B6KxMjVxBcMFKGGT/LOGO_GRUPO_REFLEX.png" alt="Grupo Reflex" className="w-48 opacity-20 brightness-0 invert" data-id="element-773" />
                      </motion.div>
                    </div>}
              </div>}

            {/* DIAGRAM SLIDE */}
            {currentData.type === 'diagram' && <div className="text-center space-y-16" data-id="element-774">
                <TypewriterText text={currentData.title} className="text-5xl md:text-6xl font-bold text-white mb-12" data-id="element-775" />
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 max-w-5xl mx-auto" data-id="element-776">
                  <motion.div initial={{
                x: -50,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 0.5
              }} className="flex-1 p-8 bg-red-900/40 border border-red-500/30 rounded-2xl backdrop-blur-md shadow-[0_0_30px_rgba(220,38,38,0.2)]" data-id="element-777">
                    <p className="text-xl font-semibold text-red-100" data-id="element-778">
                      {currentData.diagram?.left}
                    </p>
                  </motion.div>
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.8
              }} className="text-gray-400" data-id="element-779">
                    <ChevronRightIcon className="w-12 h-12 hidden md:block" data-id="element-780" />
                    <ChevronRightIcon className="w-8 h-8 md:hidden rotate-90" data-id="element-781" />
                  </motion.div>
                  <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 1
              }} className="flex-[1.5] p-10 bg-gradient-to-r from-red-900/40 to-blue-900/40 border border-white/20 rounded-2xl backdrop-blur-md shadow-2xl relative overflow-hidden" data-id="element-782">
                    <div className="absolute inset-0 bg-white/5 animate-pulse" data-id="element-783" />
                    <p className="text-2xl font-bold text-white relative z-10" data-id="element-784">
                      {currentData.diagram?.center}
                    </p>
                  </motion.div>
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 1.2
              }} className="text-gray-400" data-id="element-785">
                    <ChevronRightIcon className="w-12 h-12 hidden md:block" data-id="element-786" />
                    <ChevronRightIcon className="w-8 h-8 md:hidden rotate-90" data-id="element-787" />
                  </motion.div>
                  <motion.div initial={{
                x: 50,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} transition={{
                delay: 1.4
              }} className="flex-1 p-8 bg-blue-900/40 border border-blue-500/30 rounded-2xl backdrop-blur-md shadow-[0_0_30px_rgba(37,99,235,0.2)]" data-id="element-788">
                    <p className="text-xl font-semibold text-blue-100" data-id="element-789">
                      {currentData.diagram?.right}
                    </p>
                  </motion.div>
                </div>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1.8
            }} className="text-2xl md:text-3xl text-blue-200 font-light italic max-w-3xl mx-auto" data-id="element-790">
                  "{currentData.quote}"
                </motion.p>
              </div>}

            {/* CLOSING SLIDE */}
            {currentData.type === 'closing' && <div className="text-center space-y-12 max-w-4xl mx-auto" data-id="element-791">
                <TypewriterText text={currentData.title} className="text-5xl md:text-7xl font-bold text-white mb-8" data-id="element-792" />
                <div className="space-y-8" data-id="element-793">
                  {currentData.lines?.map((line, idx) => <motion.p key={idx} initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1 + idx * 0.5
              }} className={`text-2xl md:text-4xl leading-relaxed ${idx === 0 ? 'text-gray-300' : 'text-blue-400 font-semibold'}`} data-id="element-794">
                      {line}
                    </motion.p>)}
                </div>
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 2.5
            }} className="pt-12" data-id="element-795">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/weJPa2B6KxMjVxBcMFKGGT/LOGO_GRUPO_REFLEX.png" alt="Grupo Reflex" className="h-24 mx-auto brightness-0 invert opacity-80" data-id="element-796" />
                </motion.div>
              </div>}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="absolute bottom-8 left-0 w-full px-8 md:px-16 flex justify-between items-end z-20" data-id="element-797">
        <div className="text-sm text-gray-500 font-medium tracking-widest uppercase" data-id="element-798">
          GrupoReflex.ai
        </div>
        <div className="flex items-center gap-6" data-id="element-799">
          <div className="flex gap-2" data-id="element-800">
            {slides.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-white w-6' : 'bg-white/30 hover:bg-white/50'}`} data-id="element-801" />)}
          </div>
          <div className="text-sm text-gray-500 font-mono" data-id="element-802">
            {String(currentSlide + 1).padStart(2, '0')} /{' '}
            {String(slides.length).padStart(2, '0')}
          </div>
          <div className="flex gap-2" data-id="element-803">
            <button onClick={prevSlide} className="p-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors group" data-id="element-804">
              <ChevronLeftIcon className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" data-id="element-805" />
            </button>
            <button onClick={nextSlide} className="p-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors group" data-id="element-806">
              <ChevronRightIcon className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" data-id="element-807" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default StrategySlideshow;